import React, { useState, useEffect } from 'react';
import { fetchProducts } from '../services/api';
import ProductCard from './ProductCard';
import FilterPanel from './FilterPanel';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const ProductGrid = ({ searchQuery, category }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(category || ''); // Default to empty (All Categories)
    const [selectedTags, setSelectedTags] = useState([]);
    const [sortBy, setSortBy] = useState('createdAt');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 100; // Increased to reduce API requests

    // Random loading messages
    const loadingMessages = [
        'Getting your shopping list ready...',
        'Fetching the best deals for you...',
        'Just a moment, we are preparing your products...',
        'Hold on tight, your products are on the way...',
    ];
    const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0]);

    useEffect(() => {
        // Reset selectedCategory and currentPage when category prop changes
        setSelectedCategory(category || '');
        setCurrentPage(1);
    }, [category]);

    useEffect(() => {
        const loadProducts = async (retries = 3) => {
            try {
                setLoading(true);
                setError(null); // Clear previous errors
                const result = await fetchProducts({
                    page: currentPage,
                    limit: itemsPerPage,
                    category: selectedCategory || null,
                    tags: selectedTags.length > 0 ? selectedTags : null,
                    search: searchQuery || null,
                    sortBy: sortBy.split('_')[0],
                    sortOrder: sortBy.includes('_desc') ? 'desc' : 'asc',
                });
                setProducts(result.data);
                setTotalItems(result.pagination.total);
            } catch (err) {
                if (retries > 0) {
                    console.warn(`Retrying... ${retries} attempts left`);
                    setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
                    setTimeout(() => loadProducts(retries - 1), 2000); // Retry after 2 seconds
                } else {
                    setError(err.message || 'Error loading products');
                }
            } finally {
                setLoading(false);
            }
        };

        loadProducts();
    }, [currentPage, selectedCategory, selectedTags, searchQuery, sortBy]);

    // Handle toggling of tags
    const handleTagToggle = (tag) => {
        setSelectedTags((prev) =>
            prev.includes(tag)
                ? prev.filter((t) => t !== tag)
                : [...prev, tag]
        );
        setCurrentPage(1); // Reset pagination when filters change
    };

    // Handle clearing all filters
    const clearFilters = () => {
        setSelectedCategory('');
        setSelectedTags([]);
        setSortBy('createdAt');
        setCurrentPage(1);
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Filter Panel */}
            <div className="lg:w-72 flex-shrink-0">
                <FilterPanel
                    categories={['Apparel','Appliances','Analog Watches','Backpacks','Footwear','Gift Items','Handbags','Helmets','Smart Watches','Speakers','Sports','Yoga']}
                    selectedCategory={selectedCategory}
                    onCategoryChange={(category) => {
                        setSelectedCategory(category);
                        setCurrentPage(1); // Reset pagination when category changes
                    }}
                    tags={['new', 'sale', 'featured']}
                    selectedTags={selectedTags}
                    onTagChange={handleTagToggle}
                    sortBy={sortBy}
                    onSortChange={(sort) => {
                        setSortBy(sort);
                        setCurrentPage(1); // Reset pagination when sort changes
                    }}
                    onClearFilters={clearFilters}
                />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                {loading ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <LoadingSpinner />
                            <p className="text-gray-600 font-medium">{loadingMessage}</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <ErrorMessage message={error} />
                            <button
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                onClick={() => window.location.reload()}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col">
                        {/* Results Header */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        {totalItems > 0 ? (
                                            <>
                                                {totalItems} {totalItems === 1 ? 'Product' : 'Products'}
                                                {selectedCategory && (
                                                    <span className="text-gray-600 font-normal"> in <span className="text-gray-900">{selectedCategory}</span></span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                No products found
                                                {selectedCategory && (
                                                    <span className="text-gray-600 font-normal"> in <span className="text-gray-900">{selectedCategory}</span></span>
                                                )}
                                            </>
                                        )}
                                    </h2>
                                    {searchQuery && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            Search results for "{searchQuery}"
                                        </p>
                                    )}
                                </div>
                                {(selectedCategory || selectedTags.length > 0) && (
                                    <button
                                        onClick={clearFilters}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium px-4 py-2 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Product Grid */}
                        {products.length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-h-[300px]">
                                    {products.map((product) => (
                                        <ProductCard key={product._id} product={product} />
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalItems > itemsPerPage && (
                                    <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={Math.ceil(totalItems / itemsPerPage)}
                                            onPageChange={setCurrentPage}
                                            totalItems={totalItems}
                                            itemsPerPage={itemsPerPage}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16">
                                <div className="flex flex-col items-center justify-center text-center">
                                    <svg 
                                        className="w-24 h-24 mb-6 text-gray-300" 
                                        fill="none" 
                                        viewBox="0 0 24 24" 
                                        stroke="currentColor"
                                    >
                                        <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth="1.5" 
                                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                        />
                                    </svg>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        No products found
                                    </h3>
                                    <p className="text-gray-600 mb-6 max-w-md">
                                        {searchQuery || selectedCategory || selectedTags.length > 0
                                            ? 'Try adjusting your search or filters to find what you\'re looking for.'
                                            : 'No products are available at the moment. Check back soon!'}
                                    </p>
                                    {(selectedCategory || selectedTags.length > 0 || searchQuery) && (
                                        <button
                                            onClick={clearFilters}
                                            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                        >
                                            Clear All Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductGrid;